import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { MODULE_CATALOG, type ModuleCatalogEntry } from '@/constants/modules';
import { useTenantModulesQuery } from '@/hooks/useTenantModulesQuery';
import { useSetTenantModuleMutation } from '@/hooks/useSetTenantModuleMutation';
import { cn } from '@/lib/utils';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface ModulesTabProps {
  tenantId: string;
}

export function ModulesTab({ tenantId }: ModulesTabProps) {
  const { t } = useTranslation('modules');
  const modulesQuery = useTenantModulesQuery(tenantId);
  const toggleMutation = useSetTenantModuleMutation(tenantId);

  if (modulesQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (modulesQuery.isError === true || modulesQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          modulesQuery.refetch();
        }}
      />
    );
  }

  const serverStates = modulesQuery.data;

  function resolveEnabled(entry: ModuleCatalogEntry): boolean {
    if (entry.isCore === true) {
      return true;
    }
    const serverState = serverStates.find(function matchKey(state) {
      return state.moduleKey === entry.key;
    });
    return serverState !== undefined && serverState.isEnabled === true;
  }

  function resolveGloballyAvailable(entry: ModuleCatalogEntry): boolean {
    const serverState = serverStates.find(function matchKey(state) {
      return state.moduleKey === entry.key;
    });
    return serverState === undefined || serverState.isGloballyAvailable === true;
  }

  function handleToggle(entry: ModuleCatalogEntry, nextEnabled: boolean) {
    toggleMutation.mutate(
      { moduleKey: entry.key, isEnabled: nextEnabled },
      {
        onSuccess: function notifyToggled() {
          const name = t('catalog.' + entry.key + '.name');
          showSuccessNotification(
            nextEnabled === true
              ? t('admin.enabledNotice', { name })
              : t('admin.disabledNotice', { name })
          );
        },
        onError: function notifyError(toggleError) {
          const backendMessage = resolveApiErrorMessage(toggleError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('admin.toggleError')
          );
        },
      }
    );
  }

  return (
    <div>
      <h2 className="text-sm font-bold text-zooindigo">{t('admin.modulesTitle')}</h2>
      <p className="text-xs text-zoomuted mt-1 mb-5">{t('admin.modulesSubtitle')}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {MODULE_CATALOG.map(function renderModule(entry) {
          const isEnabled = resolveEnabled(entry);
          const isTogglingThisModule =
            toggleMutation.isPending &&
            toggleMutation.variables !== undefined &&
            toggleMutation.variables.moduleKey === entry.key;
          const isToggleDisabled =
            entry.isCore === true || entry.isComingSoon === true || toggleMutation.isPending;
          return (
            <Card
              key={entry.key}
              padding="none"
              shadow={false}
              className={cn(
                'p-4 flex items-start gap-3 transition-colors',
                isEnabled === true
                  ? 'border-zooprimary bg-zoovpalelavender bg-opacity-30'
                  : 'border-zooborder bg-zoowhite'
              )}
            >
              <div className="w-10 h-10 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
                <Icon name={entry.iconName} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-zooindigo">
                    {t('catalog.' + entry.key + '.name')}
                  </h3>
                  {entry.isCore === true ? (
                    <Badge
                      tone="success"
                      shape="tag"
                      size="sm"
                      uppercase
                      className="text-[9px] px-1.5"
                    >
                      {t('admin.badgeCore')}
                    </Badge>
                  ) : entry.isComingSoon === true ? (
                    <Badge
                      tone="muted"
                      shape="tag"
                      size="sm"
                      uppercase
                      className="text-[9px] px-1.5"
                    >
                      {t('admin.badgeComingSoon')}
                    </Badge>
                  ) : (
                    <Badge
                      tone={isEnabled === true ? 'success' : 'muted'}
                      shape="tag"
                      size="sm"
                      uppercase
                      className="text-[9px] px-1.5"
                    >
                      {isEnabled === true ? t('admin.stateEnabled') : t('admin.stateDisabled')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-zoomuted mt-1 leading-snug">
                  {t('catalog.' + entry.key + '.description')}
                </p>
                {resolveGloballyAvailable(entry) === false ? (
                  <p className="text-xs text-zoowarningtext mt-1 flex items-center gap-1">
                    <Icon name="AlertTriangle" className="shrink-0" />
                    {t('admin.globallyUnavailable')}
                  </p>
                ) : null}
              </div>
              <ToggleSwitch
                isChecked={isEnabled}
                isDisabled={isToggleDisabled}
                isPending={isTogglingThisModule}
                accessibleLabel={t('catalog.' + entry.key + '.name')}
                onToggle={function toggle() {
                  handleToggle(entry, !isEnabled);
                }}
              />
            </Card>
          );
        })}
      </div>
    </div>
  );
}
